import React, { useState } from "react";

/**
 * Button - A customizable button component with various states and icon options
 * 
 * @param {string} text - The text to display in the button (default: "Button")
 * @param {string} size - Size variant of the button (default: "Compact")
 * @param {string} state - Current state of the button (default: "Hover")
 * @param {string} type - Type of button style (default: "Secondary")
 * @param {string} variant - Button variant, affects icon placement (default: "Default")
 * @param {React.ReactNode} leadingIcon - Icon to display before text
 * @param {React.ReactNode} trailingIcon - Icon to display after text
 * @param {function} onClick - Function to call when button is clicked
 * @param {string} className - Additional CSS classes for the button
 * @param {boolean} fullWidth - Whether the button should take up full width (default: false)
 */
const Button = ({
  text = "Button",
  size = "Compact",
  state = "Hover",
  type = "Secondary",
  variant = "Default",
  leadingIcon,
  trailingIcon,
  onClick,
  className = "",
  fullWidth = false, // Add fullWidth prop with default false
}) => {
  // State to track hover and focus
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  
  // Check if button is icon-only variant
  const isIconOnly = variant === "Icon";
  
  // Determine if button is disabled
  const isDisabled = state === "Disabled";
  
  // Base button classes
  const baseClasses = "rounded-lg flex justify-center items-center gap-2";
  
  // Width class based on fullWidth prop
  const widthClass = fullWidth ? "w-full" : "inline-flex";
  
  // Padding classes based on size and variant
  let paddingClasses;
  if (isIconOnly) {
    paddingClasses = "p-2";
  } else {
    switch(size) {
      // case "Compact": paddingClasses = "px-3 py-1.5"; break;
      // case "Large": paddingClasses = "px-5 py-2.5"; break;
      default: paddingClasses = "px-4 py-2";
    }
  }
  
  // Cursor class
  const cursorClass = isDisabled ? "cursor-not-allowed" : "cursor-pointer";
  
  // State-specific styling
  const stateStyles = {
    Default: {
      container: "p-1",
      button: `outline outline-1 outline-offset-[-1px] ${type === "Secondary" ? "outline-blue-600" : "outline-neutral-200"}`,
      text: `${type === "Secondary" ? "text-blue-600" : "text-zinc-600"}`,
      icon: `${type === "Secondary" ? "bg-blue-600" : "bg-zinc-600"}`
    },
    Hover: {
      container: "p-1",
      button: "bg-blue-600",
      text: "text-white",
      icon: "bg-white"
    },
    Focused: {
      container: "p-1 rounded-xl outline outline-2 outline-offset-[-2px] outline-blue-600",
      button: "bg-blue-600",
      text: "text-white",
      icon: "bg-white"
    },
    Disabled: {
      container: "p-1",
      button: `outline outline-1 outline-offset-[-1px] outline-neutral-200`,
      text: "text-zinc-400",
      icon: "bg-zinc-400"
    }
  };
  
  // Determine the current visual state
  const determineCurrentState = () => {
    if (isDisabled) return "Disabled";
    if (isFocused) return "Focused";
    if (isHovered) return "Hover";
    return state;
  };
  
  // Get current state styling
  const currentState = stateStyles[determineCurrentState()] || stateStyles.Default;
  
  // Text styling based on size
  let fontSize;
  switch(size) {
    // case "Compact": fontSize = "text-xs"; break;
    // case "Large": fontSize = "text-base"; break;
    default: fontSize = "text-[14px]";
  }
  
  // Complete text styling
  const textClasses = `text-center justify-start ${currentState.text} ${fontSize} font-normal font-['Arial'] leading-normal`;
  
  // Icon wrapper and styling
  const iconWrapperClasses = "w-6 h-6 relative overflow-hidden";
  const iconClasses = `w-4 h-4 left-[4.25px] top-[4.25px] absolute ${currentState.icon}`;
  
  return (
    <div className={`inline-flex ${currentState.container} ${fullWidth ? 'w-full' : ''} ${className}`}>
      <div 
        data-type={variant}
        className={`${paddingClasses} ${baseClasses} ${currentState.button} ${cursorClass} ${widthClass}`}
        onClick={!isDisabled ? onClick : undefined}
        onMouseEnter={() => !isDisabled && setIsHovered(true)}
        onMouseLeave={() => !isDisabled && setIsHovered(false)}
        onFocus={() => !isDisabled && setIsFocused(true)}
        onBlur={() => !isDisabled && setIsFocused(false)}
        tabIndex={!isDisabled ? 0 : -1}
        role="button"
        aria-disabled={isDisabled}
      >
        {/* Leading Icon */}
        {(variant === "Leading" || variant === "Icon") && leadingIcon && (
          <div className={iconWrapperClasses}>
            <div className={iconClasses} />
          </div>
        )}
        
        {/* Button Text */}
        {!isIconOnly && <div className={textClasses}>{text}</div>}
        
        {/* Trailing Icon */}
        {variant === "Trailing" && trailingIcon && (
          <div className={iconWrapperClasses}>
            <div className={iconClasses} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Button;